/* =========================================================================
   Which photograph goes in which slot.

   Everything here is a real photograph from the institute's own material:
   the campus shoot in src/assets/images, the clinical and allied-health
   pages of the 2025 nursing prospectus, and the College of Law brochure.

   A slot with no honest match is left undefined on purpose — <Figure> then
   renders its labelled placeholder instead of borrowing a picture of
   something else. Those gaps are listed at the bottom of this file; drop an
   import in and the placeholder disappears.
   ========================================================================= */

/* -- campus shoot ------------------------------------------------------- */
import skillsLab from "../assets/images/aboutUs_1.JPG";
import classroom from "../assets/images/aboutUs_2.JPG";
import anatomyModel from "../assets/images/campus_1.JPG";
import bedsideTeaching from "../assets/images/campus_10.JPG";
import bpCheck from "../assets/images/campus_2.JPG";
import skeletonLab from "../assets/images/campus_3.JPG";
import library from "../assets/images/campus_4.JPG";
import wardRound from "../assets/images/campus_9.JPG";
import lectureTheatre from "../assets/images/campus_13.JPG";
import educationalTour from "../assets/images/campus_14.JPG";
import seminarHall from "../assets/images/campus_31.jpeg";
import lampLighting from "../assets/images/Event_3.JPG";

/* -- allied health, from the 2025 prospectus ---------------------------- */
import otRoom from "../assets/images/allied/ot-room.jpg";
import theatreTeam from "../assets/images/allied/theatre-team.jpg";
import technologist from "../assets/images/allied/technologist.jpg";
import labBench from "../assets/images/allied/lab.jpg";

/* -- clinical training, from the 2025 prospectus ------------------------ */
import icu from "../assets/images/clinical/icu.jpg";
import clinicalWard from "../assets/images/clinical/ward.jpg";

/* -- college of law, from the 2026–27 brochure -------------------------- */
import lawScales from "../assets/images/law/hero.jpg";
import mootCourt from "../assets/images/law/moot-court.jpg";
import lawLibrary from "../assets/images/law/library.jpg";
import courtVisit from "../assets/images/law/court-visit.jpg";
import legalAidCamp from "../assets/images/law/legal-aid-camp.jpg";
import lawInternship from "../assets/images/law/internship.jpg";
import lawWorkshop from "../assets/images/law/workshop.jpg";

/* ------------------------------------------------------------ per course */
export const COURSE_SHOT = {
  /* Medical Block */
  "bsc-nursing": skillsLab,
  gnm: bedsideTeaching,
  anm: bpCheck,

  /* Allied Healthcare */
  "bsc-medical-lab-science": labBench,
  "bsc-radiology-and-imaging-technology": technologist,
  "bsc-operation-theatre-technology": otRoom,
  /* B.Sc Optometry — no eye-care photograph exists in any source yet */

  /* Management — no business, computing, fashion or salon photographs yet */

  /* Law */
  "ba-llb": courtVisit,
  llb: lawLibrary,
};

/* ------------------------------------------------------------- per slot */
export const SHOTS = {
  medicalHero: wardRound,
  lawHero: lawScales,

  medicalStrip: [
    { src: skillsLab, alt: "Practising on a manikin in the skills lab" },
    { src: skeletonLab, alt: "An anatomy class around the skeleton" },
    { src: library, alt: "Students reading in the library" },
    { src: clinicalWard, alt: "A clinical posting on the ward" },
    { src: educationalTour, alt: "Setting off on an educational tour" },
    { src: lampLighting, alt: "The lamp lighting and oath taking ceremony" },
  ],

  /* the business, computing and craft photography does not exist yet, so
     these are the institute's own teaching spaces rather than stand-ins for
     a subject we cannot show */
  managementHero: [
    {
      src: library,
      alt: "The college library",
      ratio: "4 / 5",
      label: "Studio or workshop",
      size: "800×1000",
    },
    {
      src: seminarHall,
      alt: "A session in the seminar hall",
      ratio: "1 / 1",
      label: "Computer lab",
      size: "700×700",
    },
    {
      src: lectureTheatre,
      alt: "A lecture in progress",
      ratio: "3 / 2",
      label: "Salon practice",
      size: "900×600",
    },
  ],

  /* keyed by the practice block titles in colleges.js */
  practice: {
    "Moot court training": mootCourt,
    "Internship opportunities": lawInternship,
    "Legal workshops": lawWorkshop,
    "Additional learning activities": legalAidCamp,
  },

  /* the card for each college, shown on every sibling page and the home hero */
  collegeCard: {
    medical: bedsideTeaching,
    "allied-healthcare": otRoom,
    management: library,
    law: mootCourt,
  },

  /* the home page hero rotates through one shot per college */
  heroSlides: {
    medical: icu,
    "allied-healthcare": theatreTeam,
    management: seminarHall,
    law: mootCourt,
  },

  campusExtras: { anatomyModel, classroom, lectureTheatre, seminarHall },
};

/* -------------------------------------------------------------------------
   STILL WAITING FOR A PHOTOGRAPH — these slots render a labelled placeholder:

     B.Sc Optometry            an eye test, a trial frame, the eye clinic
     BBA                       a business class, a presentation
     BCA                       the computer lab
     B.Voc Fashion Designer    the sewing workshop, pattern cutting
     B.Voc Beauty Therapist    the salon, a therapy session

   To fill one: import the file above and add it to COURSE_SHOT under the
   course's id (the ids are in src/data/colleges.js).
------------------------------------------------------------------------- */
